import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../Components/ui/select";
import Default_WhatsApp_background from "../../../../Assets/Default_WhatsApp_background.jpeg";
import { FaFilePdf } from "react-icons/fa";

interface OperatorKey {
  channel: string;
  country: string;
  operatorId: string | number;
  label: string;
}

interface RowData {
  buttonType: string;
  buttonText: string;
  buttonTypeDropdown: string;
  websiteUrl: string;
  countryCode: string;
  callPhoneNumber: string;
  copyOfferCode: string;
}

interface MessagePreviewSectionProps {
  operatorKeys: OperatorKey[];
  selectedOperatorKey: OperatorKey | null;
  setSelectedOperatorKey: (key: OperatorKey) => void;
  operatorTemplates: Record<string, Record<string, Record<string, any>>>;
  allOperatorKeys?: OperatorKey[];
}



function extractMediaConfig(template: any, operatorId: string | number, countryIso: string) {
  console.log(`\n🔍 [EXTRACT MEDIA CONFIG] Searching:`, {
    requestedOperatorId: operatorId,
    requestedOperatorIdType: typeof operatorId,
    requestedCountryIso: countryIso.toUpperCase(),
    templateOperatorMappingId: template._operator_mapping_id,
    templateFromCountry: template._from_country_iso,
    hasMediaConfig: !!template.media_config,
    mediaConfigOperatorId: template.media_config?._operator_mapping_id,
    mediaConfigUniqueKey: template.media_config?._unique_media_key,
    mediaFileName: template.media_config?.file_name
  });

  const operatorIdNum = Number(operatorId);

  // PRIORITY 1: Direct operator_mapping_id match in template
  if (template._operator_mapping_id === operatorIdNum && template.media_config?.binary_data) {
    console.log(`✅ [MEDIA CONFIG] Direct template operator_mapping_id match: ${operatorIdNum}`);
    return template.media_config;
  }

  // PRIORITY 2: operator_mapping_id in media_config matches
  if (template.media_config?._operator_mapping_id === operatorIdNum && template.media_config?.binary_data) {
    console.log(`✅ [MEDIA CONFIG] Media config operator_mapping_id match: ${operatorIdNum}`);
    return template.media_config;
  }

  // PRIORITY 3: Exact operator + country match in template
  if (template._operator_mapping_id === operatorIdNum &&
    template._from_country_iso?.toUpperCase() === countryIso.toUpperCase() &&
    template.media_config?.binary_data) {
    console.log(`✅ [MEDIA CONFIG] Template operator + country match: ${operatorIdNum} (${countryIso})`);
    return template.media_config;
  }

  // PRIORITY 4: Operator + country match in media_config
  if (template.media_config?._operator_mapping_id === operatorIdNum &&
    template.media_config?._from_country_iso?.toUpperCase() === countryIso.toUpperCase() &&
    template.media_config?.binary_data) {
    console.log(`✅ [MEDIA CONFIG] Media config operator + country match: ${operatorIdNum} (${countryIso})`);
    return template.media_config;
  }

  // PRIORITY 5: Country match in translated template
  if (template.translated_template?.media_config?.binary_data &&
    template.translated_template.from_country_iso?.toUpperCase() === countryIso.toUpperCase()) {
    console.log(`✅ [MEDIA CONFIG] Translated template country match: ${countryIso}`);
    return template.translated_template.media_config;
  }

  // PRIORITY 6: Any available media config with binary data
  if (template.media_config?.binary_data) {
    console.log(`⚠️ [MEDIA CONFIG] Using fallback media config (no exact match found)`);
    return template.media_config;
  }

  // PRIORITY 7: Translated template media config as last resort
  if (template.translated_template?.media_config?.binary_data) {
    console.log(`⚠️ [MEDIA CONFIG] Using translated template media config as last resort`);
    return template.translated_template.media_config;
  }

  console.log(`❌ [MEDIA CONFIG] No media config found for operator ${operatorIdNum} in country ${countryIso}`);
  return null;
}


// Enhanced getBase64Src function with better validation
function getBase64Src(base64: string, type: "image" | "video" | "document"): string | null {
  if (!base64) return null;

  // If already properly formatted, return as-is
  if (base64.startsWith("data:")) return base64;

  // Clean and format base64
  const cleanBase64 = base64.replace(/^data:[^,]+,/, "");

  // Validate base64 format
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanBase64)) {
    console.warn("Invalid base64 format detected");
    return null;
  }

  if (type === "image") return `data:image/jpeg;base64,${cleanBase64}`;
  if (type === "video") return `data:video/mp4;base64,${cleanBase64}`;
  if (type === "document") return `data:application/pdf;base64,${cleanBase64}`;
  return null;
}

function extractButtonRows(buttons: any[], qr: any[] = []): RowData[] {
  if (!Array.isArray(buttons)) return [];
  return buttons
    .map((btn: any) => {
      const baseRow = {
        buttonText: "",
        buttonTypeDropdown: "",
        websiteUrl: "",
        countryCode: "",
        callPhoneNumber: "",
        copyOfferCode: "",
      };

      if (btn.type === "URL") {
        return {
          ...baseRow,
          buttonType: "View Website",
          buttonText: btn.text || "", // Make sure buttonText is set
          buttonTypeDropdown: "View Website",
          websiteUrl: btn.url || "",
        };
      } else if (btn.type === "PHONE_NUMBER") {
        const phoneNumber = btn.phone_number || "";
        return {
          ...baseRow,
          buttonType: "Call Phone Number",
          buttonText: btn.text || "", // Make sure buttonText is set
          buttonTypeDropdown: "Call Phone Number",
          countryCode: phoneNumber.substring(0, 3),
          callPhoneNumber: phoneNumber.substring(3),
        };
      } else if (btn.type === "COPY_CODE") {
        return {
          ...baseRow,
          buttonType: "COPY_CODE",
          buttonText: "Copy", // FIXED: Set buttonText to "Copy" for COPY_CODE buttons
          copyOfferCode: btn.example || "", // Use example field for the offer code
        };
      } else if (btn.type === "QUICK_REPLY") {
        // Handle translated template format where COPY_CODE becomes QUICK_REPLY
        const isActuallyCopyCode = btn.text === "Copy";

        if (isActuallyCopyCode) {
          return {
            ...baseRow,
            buttonType: "COPY_CODE",
            buttonText: "Copy", // FIXED: Set buttonText to "Copy"
            copyOfferCode: btn.example || btn.text || "", // Try to get the offer code
          };
        } else {
          // Regular quick reply
          const qrData = qr.find((q: any) => q.label === btn.text);
          return {
            ...baseRow,
            buttonType: "QUICK_REPLY",
            buttonText: btn.text || "",
            qrResponse: qrData?.response || "",
            qrOptOut: qrData?.opt_out ?? false,
          };
        }
      }

      return baseRow;
    })
    .filter(Boolean) as RowData[];
}

// FIXED: Template extraction with proper media handling
// FIXED: Template extraction with proper operator mapping ID handling
function extractTemplateData(channel: string, template: any, operatorId: string | number, countryIso: string) {
  console.log(`\n🔧 [EXTRACT TEMPLATE DATA] Processing:`, {
    channel,
    operatorId,
    operatorIdType: typeof operatorId,
    countryIso,
    templateType: template.message ? 'SMS' : 'WhatsApp',
    templateOperatorMappingId: template._operator_mapping_id,
    templateFromCountry: template._from_country_iso,
    templateUniqueKey: template._unique_template_key
  });

  if (!template || typeof template !== "object") {
    console.log(`❌ [EXTRACT TEMPLATE DATA] Invalid template`);
    return {
      channelType: channel.toLowerCase() === "sms" ? "SMS" as const : "WhatsApp" as const,
      bodyText: "",
      footerText: "",
      selectedOption: "",
      textInput: "",
      rows: [],
      base64: "",
      mediaConfig: null,
    };
  }

  const operatorIdNum = Number(operatorId);

  // Verify this template belongs to the requested operator
  if (template._operator_mapping_id && template._operator_mapping_id !== operatorIdNum) {
    console.log(`⚠️ [EXTRACT TEMPLATE DATA] Template operator mismatch: template=${template._operator_mapping_id}, requested=${operatorIdNum}`);
    // Continue processing but log the mismatch
  }

  if (channel.toLowerCase() === "sms") {
    let smsText = template.message || template.template?.message || template.translated_template?.template?.message || "";
    console.log(`📱 [SMS EXTRACT] Operator ${operatorIdNum}: Message="${smsText.substring(0, 50)}..."`);
    return {
      channelType: "SMS" as const,
      bodyText: smsText,
      footerText: "",
      selectedOption: "",
      textInput: "",
      rows: [],
      base64: "",
      mediaConfig: null,
    };
  }

  // WhatsApp logic
  let components: any[] = template.components || template.template?.components || template.translated_template?.template?.components || [];

  console.log(`📱 [WHATSAPP EXTRACT] Operator ${operatorIdNum}: Components found: ${components.length}`);

  if (!components.length) {
    console.log(`📱 [WHATSAPP EXTRACT] Operator ${operatorIdNum}: No components, using direct fields`);
    return {
      channelType: "WhatsApp" as const,
      bodyText: template?.bodyText || template?.body || "",
      footerText: template?.footerText || template?.footer || "",
      selectedOption: template?.headerText || template?.header ? "text" : "",
      textInput: template?.headerText || template?.header || "",
      rows: template?.buttons ? extractButtonRows(template.buttons) : [],
      base64: template?.Base64 || "",
      mediaConfig: template.media_config,
    };
  }

  const header = components.find((c) => c.type === "HEADER") || {};
  const body = components.find((c) => c.type === "BODY") || {};
  const footer = components.find((c) => c.type === "FOOTER") || {};
  const buttons = components.find((c) => c.type === "BUTTONS") || {};

  let selectedOption = header.format ? String(header.format).toLowerCase() : "";
  let headerText = (selectedOption === "text" && header.text) ? header.text : "";
  let rows = extractButtonRows(buttons.buttons || [], template?.qr || []);
  console.log(`[QR CHECK - MessagePreviewCard] channel=${channel} operatorId=${operatorId} | template.qr=`, JSON.stringify(template?.qr), `| rows=`, JSON.stringify(rows));
  // CRITICAL: Use the extractMediaConfig function to get the CORRECT media for this specific operator
  let mediaConfig: any = extractMediaConfig(template, operatorIdNum, countryIso);

  console.log(`📷 [WHATSAPP MEDIA EXTRACT] Operator ${operatorIdNum}:`, {
    selectedOption,
    hasMediaConfig: !!mediaConfig,
    mediaConfigFileName: mediaConfig?.file_name,
    mediaConfigOperatorId: mediaConfig?._operator_mapping_id,
    mediaConfigUniqueKey: mediaConfig?._unique_media_key,
    templateBase64Length: template.Base64?.length,
    templateOperatorId: template._operator_mapping_id
  });

  // Build base64 from the CORRECT media config for this operator
  let base64 = "";
  if (mediaConfig && mediaConfig.binary_data) {
    const fileType = mediaConfig.file_type || "image/jpeg";
    const binaryData = mediaConfig.binary_data;

    if (binaryData.startsWith("data:")) {
      base64 = binaryData;
    } else {
      base64 = `data:${fileType};base64,${binaryData}`;
    }
    console.log(`📷 [BASE64 BUILT] Operator ${operatorIdNum}: Length=${base64.length}, UniqueKey=${mediaConfig._unique_media_key}`);
  } else if (template.Base64) {
    base64 = template.Base64;
    console.log(`📷 [BASE64 FALLBACK] Operator ${operatorIdNum}: Using template.Base64, length=${base64.length}`);
  }

  const result = {
    channelType: "WhatsApp" as const,
    bodyText: body.text || "",
    footerText: footer.text || "",
    selectedOption,
    textInput: headerText,
    rows,
    base64,
    mediaConfig,
  };

  console.log(`✅ [WHATSAPP EXTRACT COMPLETE] Operator ${operatorIdNum}:`, {
    selectedOption: result.selectedOption,
    bodyText: result.bodyText?.substring(0, 30),
    footerText: result.footerText,
    base64Length: result.base64?.length,
    mediaConfigFile: result.mediaConfig?.file_name,
    mediaConfigUniqueKey: result.mediaConfig?._unique_media_key,
    rowsCount: result.rows?.length
  });

  return result;
}

const MessagePreviewSection: React.FC<MessagePreviewSectionProps> = ({
  operatorKeys,
  selectedOperatorKey,
  setSelectedOperatorKey,
  operatorTemplates,
  allOperatorKeys,
}) => {
  const [bodyText, setBodyText] = useState<string>("");
  const [footerText, setFooterText] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [textInput, setTextInput] = useState<string>("");
  const [rows, setRows] = useState<RowData[]>([]);
  const [channelType, setChannelType] = useState<"SMS" | "WhatsApp" | null>(null);
  const [base64, setBase64] = useState<string | undefined>(undefined);
  const [mediaConfig, setMediaConfig] = useState<any>(null);

  const dropdownOperatorKeys = allOperatorKeys || operatorKeys;

  const setOperatorKey = (v: any) => {
    debugger;
    console.log(`v key: ${JSON.stringify(v)}`);
    const newKey = JSON.parse(v);
    setSelectedOperatorKey(newKey);
  }

  useEffect(() => {
    console.log("🚀 [Operator Templates] Received:", operatorTemplates);
  }, [operatorTemplates]);

  useEffect(() => {
    console.log(`\n🔄 [PREVIEW UPDATE] Effect triggered:`, {
      selectedOperatorKey,
      hasOperatorTemplates: Object.keys(operatorTemplates).length > 0,
      operatorTemplatesKeys: Object.keys(operatorTemplates)
    });

    if (!selectedOperatorKey) {
      console.log(`❌ [PREVIEW UPDATE] No selected operator key`);
      setSelectedOption("");
      setTextInput("");
      setBodyText("");
      setFooterText("");
      setRows([]);
      setChannelType(null);
      setBase64(undefined);
      setMediaConfig(null);
      return;
    }

    const { channel, country, operatorId } = selectedOperatorKey;
    console.log(`🔍 [PREVIEW UPDATE] Looking for template:`, {
      channel,
      country,
      operatorId,
      operatorIdType: typeof operatorId
    });

    // Debug: Check what's available in operatorTemplates
    console.log(`📚 [TEMPLATES AVAILABLE]:`, {
      channels: Object.keys(operatorTemplates),
      channelExists: !!operatorTemplates[channel],
      countriesInChannel: operatorTemplates[channel] ? Object.keys(operatorTemplates[channel]) : [],
      countryExists: !!(operatorTemplates[channel] && operatorTemplates[channel][country]),
      operatorsInCountry: (operatorTemplates[channel] && operatorTemplates[channel][country]) ?
        Object.keys(operatorTemplates[channel][country]) : []
    });

    // Try different operator ID formats to find the template
    let template = null;
    let foundWithKey = null;

    // Try exact match first
    if (operatorTemplates[channel]?.[country]?.[operatorId]) {
      debugger;
      template = operatorTemplates[channel][country][operatorId];
      foundWithKey = operatorId;
      console.log(`✅ [TEMPLATE FOUND] Exact match with key: ${operatorId}`);
    }

    // Try string version
    if (!template) {
      debugger;
      const stringKey = String(operatorId);
      if (operatorTemplates[channel]?.[country]?.[stringKey]) {
        template = operatorTemplates[channel][country][stringKey];
        foundWithKey = stringKey;
        console.log(`✅ [TEMPLATE FOUND] String match with key: ${stringKey}`);
      }
    }

    // Try number version
    if (!template) {
      const numberKey = Number(operatorId);
      if (!isNaN(numberKey) && operatorTemplates[channel]?.[country]?.[numberKey]) {
        template = operatorTemplates[channel][country][numberKey];
        foundWithKey = numberKey;
        console.log(`✅ [TEMPLATE FOUND] Number match with key: ${numberKey}`);
      }
    }

    // Debug all available operators in this country
    if (!template && operatorTemplates[channel]?.[country]) {
      console.log(`🔍 [DEBUG] All available operators in ${channel}/${country}:`);
      Object.entries(operatorTemplates[channel][country]).forEach(([key, templ]: [string, any]) => {
        console.log(`  - Key: "${key}" (type: ${typeof key}), Template:`, {
          operatorMappingId: templ._operator_mapping_id,
          fromCountryIso: templ._from_country_iso,
          hasMediaConfig: !!templ.media_config,
          mediaFileName: templ.media_config?.file_name,
          messagePreview: templ.message?.substring(0, 30)
        });
      });
    }

    if (!template) {
      console.log(`❌ [TEMPLATE NOT FOUND] No template found for ${channel}/${country}/${operatorId}`);
      setSelectedOption("");
      setTextInput("");
      setBodyText("");
      setFooterText("");
      setRows([]);
      setChannelType(channel.toLowerCase() === "sms" ? "SMS" : "WhatsApp");
      setBase64(undefined);
      setMediaConfig(null);
      return;
    }

    console.log(`🎯 [TEMPLATE PROCESSING] Found template with key ${foundWithKey}:`, {
      templateOperatorId: template._operator_mapping_id,
      templateFromCountry: template._from_country_iso,
      templateType: template.message ? 'SMS' : 'WhatsApp',
      hasMediaConfig: !!template.media_config,
      mediaFileName: template.media_config?.file_name,
      mediaConfigOperatorId: template.media_config?._operator_mapping_id,
      base64Length: template.Base64?.length,
      messagePreview: template.message?.substring(0, 50)
    });

    // Process the template
    const preview = extractTemplateData(channel, template, operatorId, country);

    console.log(`📋 [PREVIEW EXTRACTED]:`, {
      channelType: preview.channelType,
      selectedOption: preview.selectedOption,
      bodyText: preview.bodyText?.substring(0, 50),
      footerText: preview.footerText,
      base64Length: preview.base64?.length,
      mediaConfigFileName: preview.mediaConfig?.file_name,
      rowsCount: preview.rows?.length
    });

    setChannelType(preview.channelType);
    setSelectedOption(preview.selectedOption);
    setTextInput(preview.textInput);
    setBodyText(preview.bodyText);
    setFooterText(preview.footerText);
    setRows(preview.rows);
    setBase64(preview.base64);
    setMediaConfig(preview.mediaConfig);

    console.log(`✅ [PREVIEW UPDATE] State updated for operator ${operatorId}`);

  }, [selectedOperatorKey, operatorTemplates]);

  const textAreaIcon = () => (
    <div className="relative w-8 h-8 flex justify-center items-center">
      <div className="absolute rounded w-[3px] h-8 transform rotate-45" style={{ borderLeft: "3px solid #64748B" }} />
      <div className="absolute w-[3px] h-4 rounded transform rotate-45 translate-x-2 translate-y-2" style={{ borderLeft: "3px solid #64748B" }} />
    </div>
  );

  const renderFormattedText = (text: string) =>
    text
      .replace(/\*([^*]+)\*/g, "<strong>$1</strong>")
      .replace(/_([^_]+)_/g, "<em>$1</em>")
      .replace(/~([^~]+)~/g, "<s>$1</s>")
      .replace(/`([^`]+)`/g, '<code style="background: #f1f1f1; padding: 2px 4px; border-radius: 3px;">$1</code>')
      .replace(/\{\{(\d+)\}\}/g, '<span style="background: #e3f2fd; padding: 2px 4px; border-radius: 3px; color: #1976d2;">{{$1}}</span>');

  const formatText = (text: string) => {
    return text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>")
      .replace(/`(.*?)`/g, "<code>$1</code>");
  };

  useEffect(() => {
    renderPreview();
  }, [selectedOperatorKey])

  const renderPreview = () => {
    const isOperatorAllocated = !!selectedOperatorKey;
    const isBodyEmpty = !bodyText?.trim();
    const isFooterEmpty = !footerText?.trim();
    const noContentSelected =
      !isOperatorAllocated ||
      (channelType === "SMS"
        ? isBodyEmpty
        : (!selectedOption && isBodyEmpty && isFooterEmpty));
    const channel = selectedOperatorKey ? selectedOperatorKey.channel : "";

    // SMS Preview
    if (channelType === "SMS") {
      return (
        <div className="flex flex-col h-full w-full bg-[#F0F4F8] rounded-2xl p-4 overflow-y-auto">
          <div className="w-full rounded p-0 mt-0 min-h-fit text-left">
            <div
              className="whitespace-pre-wrap break-words text-sm text-[#020617]"
              dangerouslySetInnerHTML={{ __html: formatText(bodyText) }}
            />
          </div>
        </div>
      );
    }

    // WhatsApp Preview
    const whatsappStyle = channel?.toLowerCase() === "whatsapp" && !noContentSelected
      ? {
        backgroundImage: `url(${Default_WhatsApp_background})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundColor: "#f0f0f0"
      }
      : {
        backgroundColor: "#e5e7eb"
      };

    return (
      <div
        className="flex rounded-[20px] flex-col justify-between w-full h-auto bottom-0 p-4 overflow-hidden"
        style={whatsappStyle}
      >
        {noContentSelected ? (
          <div className="flex w-full h-[540px] items-center justify-center">
            <div className="text-center">
              {textAreaIcon()}
              <div className="mt-6">
                <p className="text-xl font-semibold">Mobile screen</p>
              </div>
              <div className="w-[125px] mt-2" style={{ fontWeight: 500, fontSize: "14px" }}>
                <p>Preview varies based on platform selection</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Header Media */}
            <div className="flex justify-center">
              {selectedOption === "image" && base64 && (
                <div className="w-full">
                  <img
                    src={getBase64Src(base64, "image") || ""}
                    alt="Header Image"
                    className="w-full h-40 mt-2 object-cover mx-auto rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              {selectedOption === "video" && base64 && (
                <div className="w-full">
                  <video
                    controls
                    className="w-full h-[200px] object-cover mx-auto rounded-lg"
                    src={getBase64Src(base64, "video") || ""}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
              {selectedOption === "document" && base64 && (
                <div className="w-full">
                  <div className="mb-2 p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                    <FaFilePdf className="w-6 h-6 text-red-500" />
                    <div>
                      <div className="text-sm font-medium">
                        {mediaConfig?.file_name || "Document.pdf"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {mediaConfig?.file_length ? `${(parseInt(mediaConfig.file_length) / 1024).toFixed(1)} KB` : "PDF Document"}
                      </div>
                    </div>
                  </div>
                  <embed
                    src={getBase64Src(base64, "document") || ""}
                    type="application/pdf"
                    width="100%"
                    height="200px"
                    className="rounded-lg"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
              {(selectedOption === "image" || selectedOption === "video" || selectedOption === "document") && !base64 && (
                <div className="text-gray-400 text-center w-full h-[200px] flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <div>
                    <div className="mb-2">
                      {selectedOption === "image" && "📷"}
                      {selectedOption === "video" && "🎥"}
                      {selectedOption === "document" && "📄"}
                    </div>
                    <div>No {selectedOption} uploaded</div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 text-black w-full text-left">
              {selectedOption === "text" && textInput && (
                <p className="w-full text-base font-sans p-2 font-bold" style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                  {textInput}
                </p>
              )}
              {!isBodyEmpty && (
                <div
                  className="mb-2 whitespace-pre-wrap break-words text-sm leading-relaxed p-2"
                  dangerouslySetInnerHTML={{ __html: renderFormattedText(bodyText) }}
                />
              )}
            </div>
            {!isFooterEmpty && (
              <div className="border-t mt-2 pt-2 text-center">
                <p className="font-semibold text-black" style={{ fontSize: "14px" }}>{footerText}</p>
              </div>
            )}
            {rows.length > 0 && (
              <div className="border-t mt-2 pt-2 pb-2 text-center space-y-1">
                {rows.map((row, index) => (
                  <div key={index} className="border-b border-gray-200 last:border-b-0 pb-1">
                    <p className="font-serif text-blue-400" style={{ fontSize: "14px" }}>
                      {row.buttonType === "View Website" ? (
                        <a href={row.websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {row.buttonText}
                        </a>
                      ) : (
                        row.buttonText
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-[400px] space-y-3">
      <Select
        value={selectedOperatorKey ? JSON.stringify(selectedOperatorKey) : ""}
        onValueChange={(v) => {
          setOperatorKey(v);
        }}
      >
        <SelectTrigger className="w-full bg-[#F8FAFC] text-[#020617] h-[32px] px-2 text-sm">
          <SelectValue placeholder="Select Preview" />
        </SelectTrigger>
        <SelectContent>
          {dropdownOperatorKeys.length === 0 ? (
            <SelectItem value="__no_operator__" disabled className="text-sm text-[#64748B] justify-center">
              No operators allocated
            </SelectItem>
          ) : (
            dropdownOperatorKeys.map((key: OperatorKey) => (
              <SelectItem
                key={key.operatorId + "-" + key.country + "-" + key.channel}
                value={JSON.stringify(key)}
                className="text-sm"
              >
                {key.label} ({key.country})
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      <div className="border rounded-lg p-3 w-[400px] h-auto flex flex-col">
        <h2 className="text-sm font-semibold text-[#020617] mb-3">
          Message preview
          {/* {selectedOperatorKey && (
            <span className="text-xs text-gray-500 ml-2">
              (Operator: {selectedOperatorKey.operatorId})
            </span>
          )} */}
        </h2>
        <div className="flex flex-col items-center rounded-[20px] w-full">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default MessagePreviewSection;
